import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { KeyRound, Loader2, MailCheck, CheckCircle2, AlertTriangle } from "lucide-react";
import { LanguageSelector } from "@/components/common/LanguageSelector";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordField } from "@/components/profile/PasswordField";
import { sec, passwordChecks } from "@/lib/securityApi";
import { useST } from "@/i18n/securityText";

const Shell = ({ icon: Icon, title, sub, children }) => (
  <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col">
    <div className="flex items-center justify-between p-4">
      <Link to="/"><img src="/venka-logo.png" alt="VENKA" className="h-9 w-auto" /></Link>
      <LanguageSelector />
    </div>
    <div className="flex-1 grid place-items-center px-4 pb-16">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-6 text-center">
          <span className="grid h-12 w-12 place-items-center rounded-xl bg-venka-blue/15 text-venka-blue mb-3"><Icon className="h-6 w-6" /></span>
          <h1 className="text-xl font-bold">{title}</h1>
          {sub && <p className="text-sm text-slate-400 mt-1">{sub}</p>}
        </div>
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6">{children}</div>
      </div>
    </div>
  </div>
);

const loginPath = (from) => (from === "portal" ? "/portal/login" : "/account/login");

export const ForgotPasswordPage = () => {
  const { st } = useST();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await sec.forgot(email.trim()); } catch (err) { /* same answer either way */ }
    setSent(true); setBusy(false);
  };
  if (sent) {
    return (
      <Shell icon={MailCheck} title={st("sentTitle")}>
        <p className="text-sm text-slate-300" data-testid="forgot-sent">{st("sentText", { email })}</p>
        <Link to={loginPath(params.get("from"))} className="mt-5 block text-center text-sm text-venka-blue hover:underline">{st("back")}</Link>
      </Shell>
    );
  }
  return (
    <Shell icon={KeyRound} title={st("forgotTitle")} sub={st("forgotSub")}>
      <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
        <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" autoComplete="email"
          className="h-11 bg-slate-950 border-slate-700" data-testid="forgot-email" />
        <Button type="submit" disabled={busy} className="w-full h-11 rounded-xl bg-venka-blue text-white hover:bg-venka-blue-dark font-semibold" data-testid="forgot-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : st("send")}
        </Button>
        <Link to={loginPath(params.get("from"))} className="block text-center text-sm text-slate-400 hover:text-slate-200">{st("back")}</Link>
      </form>
    </Shell>
  );
};

export const ResetPasswordPage = () => {
  const { st } = useST();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [valid, setValid] = useState(null);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);
  useEffect(() => { sec.resetCheck(token).then((r) => setValid(r.valid)).catch(() => setValid(false)); }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) { setError(st("mismatch")); return; }
    setBusy(true); setError("");
    try { setDone(await sec.reset(token, pw)); } catch (err) { setError(err?.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };

  if (valid === null) return <Shell icon={KeyRound} title={st("resetTitle")}><div className="grid place-items-center py-6"><Loader2 className="h-5 w-5 animate-spin text-venka-blue" /></div></Shell>;
  if (done) {
    return (
      <Shell icon={CheckCircle2} title={st("resetDone")}>
        <Link to={done.role === "customer" ? "/account/login" : "/portal/login"} data-testid="reset-signin"
          className="block w-full rounded-xl bg-venka-blue py-2.5 text-center text-sm font-semibold text-white">{st("signIn")}</Link>
      </Shell>
    );
  }
  if (!valid) {
    return (
      <Shell icon={AlertTriangle} title={st("resetInvalid")}>
        <Link to="/forgot-password" className="block text-center text-sm text-venka-blue hover:underline">{st("askAgain")}</Link>
      </Shell>
    );
  }
  return (
    <Shell icon={KeyRound} title={st("resetTitle")}>
      <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
        <PasswordField label={st("newPw")} value={pw} onChange={setPw} meter testid="reset-password" />
        <PasswordField label={st("confirm")} value={pw2} onChange={setPw2} testid="reset-password2" />
        {error && <p className="text-sm text-red-400">{error}</p>}
        <Button type="submit" disabled={busy || !passwordChecks(pw).ok || !pw2} className="w-full h-11 rounded-xl bg-venka-blue text-white hover:bg-venka-blue-dark font-semibold" data-testid="reset-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : st("resetBtn")}
        </Button>
      </form>
    </Shell>
  );
};
